import React, { useState, useEffect } from 'react';
import { FloatingInput, ShapesDecorativeBorder } from 'src/components';
import { Redirect } from 'react-router-dom';
import { Field, Form, Formik } from 'formik';
import TealiumEvents from 'src/helpers/tealium-events';
import { SelfServiceFormBackground } from '../../self-service-form-background/self-service-form-background';
import { getOtpForSession, setOtpForSession } from 'src/services/user.service';
import { onboardingBrokerLoginRoute, postLogoutRoute } from 'src/routing/routes';
import { FormContainer } from '../../form-container/form-container';
import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import ButtonPrimary from '../component/button-primary/button-primary';
import ButtonSecondary from '../component/button-secondary/button-secondary';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import useBrokerLogin from 'src/hooks/useBrokerLogin';
import './broker-change-password.scss';

export default function BrokerChangePassword() {
  const handleTechnicalError = useTechnicalErrorHandler();
  const { changePassword } = useBrokerLogin();
  const [redirect, setRedirect] = useState();

  useEffect(() => {
    TealiumEvents.loginSetPasswordPageLoadNewStyle();
  }, []);

  if (redirect) {
    return <Redirect push to={redirect} />;
  }

  function isFormValid(values) {
    if (!values.newPassword) {
      return false;
    }

    if (values.newPassword !== values.confirmPassword) {
      return false;
    }

    let isValid = true;

    passwordValidators.forEach((validator) => {
      if (!validator.validate(values.newPassword)) {
        isValid = false;
        return;
      }
    });

    return isValid;
  }

  async function validate(values) {
    const errors = {};

    if (values.newPassword.length <= 8) {
      errors.newPassword = 'Password must be longer than 8 characters.';
    }

    if (values.newPassword) {
      passwordValidators.forEach((validator) => {
        if (!validator.validate(values.newPassword)) {
          errors.newPassword = validator.description;
          return errors;
        }
      });
    }

    if (values.confirmPassword) {
      if (values.newPassword !== values.confirmPassword) {
        errors.confirmPassword = 'Password does not match';
        return errors;
      }
    }

    return errors;
  }

  async function submit(values, helpers) {
    const { setSubmitting } = helpers;
    let allValidatorsPassed = true;
    passwordValidators.forEach((validator) => {
      if (!validator.validate(values.newPassword)) {
        allValidatorsPassed = false;
        return;
      }
    });
    if (!allValidatorsPassed) {
      return;
    }

    const abortController = new AbortController();

    const { error } = await changePassword(
      {
        ...values,
      },
      abortController,
    );

    if (!abortController.signal.aborted) {
      setSubmitting(false);
      switch (error) {
        case null:
        case undefined:
          setOtpForSession(undefined);
          TealiumEvents.loginSetPasswordClickSetPassword(postLogoutRoute);
          setRedirect(onboardingBrokerLoginRoute);
          break;
        default:
          handleTechnicalError(
            'unrecognized error received in response to change-password POST request',
          );
      }
    }
  }

  const passwordValidators = [
    {
      description: 'One number',
      validate: (value) => {
        return /\d/.test(value);
      },
    },
    {
      description: 'One uppercase character',
      validate: (value) => {
        return /[A-Z]/.test(value);
      },
    },
    {
      description: 'One lowercase character',
      validate: (value) => {
        return /[a-z]/.test(value);
      },
    },
    {
      description: 'One special character',
      validate: (value) => {
        return /[!"#\$%&'\(\)\*\+,-\.\/:;<=>\?\@\[\\\]\^_`{\|}~]/.test(value);
      },
    },
    {
      description: 'Eight characters minimum',
      validate: (value) => {
        return value && value.length >= 8;
      },
    },
  ];

  return (
    <div className="broker-change-password">
      <SelfServiceFormBackground className={'dark-background'}>
        <ShapesDecorativeBorder>
          <Formik
            initialValues={{
              oldPassword: getOtpForSession(),
              newPassword: '',
            }}
            onSubmit={submit}
            validateOnChange={true}
            validate={validate}
          >
            {({ isSubmitting, setFieldTouched, values }) => {
              return (
                <FormContainer classname={'container-white'}>
                  <Form>
                    <div className="has-text-centered" data-testid="change-password-form">
                      <div
                        className="form-heading heading-four"
                        automationid="Self_Service_Forgot_Password_Create_Passwor_H1_Change_Password_Heading"
                      >
                        Create a password
                      </div>
                    </div>
                    <Field
                      name="newPassword"
                      type="password"
                      component={MiniAppFocusWrapper}
                      innerComponent={FloatingInput}
                      className="password-field"
                      fieldType="standard"
                      placeholder="New password (8 characters)"
                      automationIdPrefix="Self_Service_Forgot_Password_Create_Password_Change_Password_New"
                      onInput={() => setFieldTouched('newPassword', true)}
                    />

                    <Field
                      name="confirmPassword"
                      type="password"
                      component={MiniAppFocusWrapper}
                      innerComponent={FloatingInput}
                      className="password-field"
                      fieldType="standard"
                      placeholder="Confirm Password"
                      automationIdPrefix="Self_Service_Forgot_Password_Create_Password_Change_Password_New"
                      onInput={() => setFieldTouched('confirmPassword', true)}
                    />

                    <ButtonPrimary
                      className="password-button"
                      type="submit"
                      disabled={isSubmitting || !isFormValid(values)}
                      automationid="Self_Service_Forgot_Password_Create_Password_Button_Change_Password"
                    >
                      Set password
                    </ButtonPrimary>

                    <ButtonSecondary
                      disabled={isSubmitting}
                      className="cancel-button"
                      automationid="Reset_Password_Button_Back"
                      onClick={() => {
                        setRedirect(onboardingBrokerLoginRoute);
                      }}
                    >
                      Cancel
                    </ButtonSecondary>
                  </Form>
                </FormContainer>
              );
            }}
          </Formik>
        </ShapesDecorativeBorder>
      </SelfServiceFormBackground>
    </div>
  );
}
